import {
    Children,
    cloneElement,
    ComponentProps,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';
import noop from 'lodash/noop';
import styled from 'styled-components';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';

const Indicator = styled.div.withConfig({ displayName: 'Indicator' })<{ $hasTransition: boolean }>`
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    background-color: var(--color-primary);
    transition: ${({ $hasTransition }) =>
        $hasTransition ? 'all 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms' : 'none'};
`;

interface TabProps extends Omit<ComponentProps<typeof Box>, 'value'> {
    value: any;
}

export const Tab = styled(Box)
    .withConfig({ displayName: 'Tab' })
    .attrs<TabProps>((props) => ({
        role: 'tab',
        cursor: 'pointer',
        px: 1,
        py: 1,
        ...props,
    }))<TabProps>`
    color: var(--color-secondary);

    &[aria-selected='true'] {
        color: var(--color-primary);
    }
`;

interface TabsProps extends ComponentProps<typeof Box> {
    value: any;
    onChange?: (value: any) => void;
    children: ReactElement<TabProps> | Array<ReactElement<TabProps>>;
}

export const Tabs = (props: TabsProps) => {
    const { value, onChange = noop, children, ...other } = props;
    const indicatorRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [hasTransition, setHasTransition] = useState(false);

    useEffect(() => {
        const onResizeIndicator = () => {
            if (!indicatorRef.current || !containerRef.current) {
                return;
            }

            const activeElement = containerRef.current.querySelector('[aria-selected=true]') as
                | HTMLElement
                | undefined;

            if (activeElement) {
                indicatorRef.current.style.setProperty('width', activeElement.offsetWidth + 'px');
                indicatorRef.current.style.setProperty(
                    'transform',
                    `translateX(${activeElement.offsetLeft}px)`,
                );
            }
        };

        onResizeIndicator();

        // Включаем анимацию после первоначальной установки позиции
        if (!hasTransition) {
            // Используем requestAnimationFrame чтобы убедиться, что стили применились
            requestAnimationFrame(() => {
                setHasTransition(true);
            });
        }

        window.addEventListener('resize', onResizeIndicator);

        return () => {
            window.removeEventListener('resize', onResizeIndicator);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indicatorRef.current, containerRef.current, value]);

    return (
        <Box overflow="auto" position="relative" borderBottom="1px solid var(--divider)" {...other}>
            <Flex
                ref={containerRef}
                direction="row"
                flexWrap="nowrap"
                role="tablist"
                alignItems="center"
            >
                {(Children.toArray(children) as Array<ReactElement<TabProps>>).map((element) =>
                    cloneElement(element, {
                        'aria-selected': value === element.props.value,
                        onClick: () => onChange(element.props.value),
                    }),
                )}
            </Flex>
            <Indicator ref={indicatorRef} $hasTransition={hasTransition} />
        </Box>
    );
};
